import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
    DragEndEvent,
} from "@dnd-kit/core";
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useTranslation } from "react-i18next";
import { RefreshProjectProps, SortableAudioItemProps } from "../../types/screensTypes";
import { AudioAI, Project } from "../../types/types";
import { useGlobalState } from "../../context/useGlobalState";
import useCompositionStates, { CompositionState } from "../../hooks/useCompositionStates";

import {
    deleteProject,
    getProject,
    deleteItemFromProject,
    updateAudioAIName,
    updateAudioAIPositionInProjects,
    updateProject,
    getVoices,
} from "../../api";
import { AudioLanguageGroup, SortMode, groupAudiosByPoint, sortAudios } from "../../utils/audioLanguageGroups";
import TextInput from "../../components/ui/TextInput";
import AudioPlayerRewind from "../../components/common/AudioPlayer";
import Icon from "../../components/common/Icon";
import Dropdown from "../../components/common/Dropdown";
import ToggleSwitch from "../../components/common/ToggleSwitch";
import TextModal from "../../components/modals/ProjectTextModal";
import DeleteModal from "../../components/modals/DeleteModal";
import s from "../../components/common/ContentSurface.module.css";
import audioFooter from "../../components/common/AudioPlayerFooter.module.css";

// Type for voice lookup - using minimal interface since getVoices returns transformed data
interface VoiceLookupItem {
    id: string;
    name: string;
}

// Helper function to find voice in lookup by ID, voiceCloneDbId, or promptVoiceDbId
const findVoiceInLookup = (
    voiceId: string | null | undefined,
    voiceCloneId: string | null | undefined,
    voicePromptId: string | null | undefined,
    voiceLookupById: Map<string, VoiceLookupItem>,
    voiceLookupByCloneId: Map<string, VoiceLookupItem>,
    voiceLookupByPromptVoiceId: Map<string, VoiceLookupItem>,
): VoiceLookupItem | undefined => {
    if (voiceId) {
        const voice = voiceLookupById.get(voiceId);
        if (voice) return voice;
    }
    if (voiceCloneId) {
        const voice = voiceLookupByCloneId.get(voiceCloneId);
        if (voice) return voice;
    }
    if (voicePromptId) {
        const voice = voiceLookupByPromptVoiceId.get(voicePromptId);
        if (voice) return voice;
    }
    return undefined;
};

// Helper function to render voice information
const renderVoiceInfo = (
    audio: AudioAI,
    voiceLookupById: Map<string, VoiceLookupItem>,
    voiceLookupByCloneId: Map<string, VoiceLookupItem>,
    voiceLookupByPromptVoiceId: Map<string, VoiceLookupItem>,
    languagesAI: any[],
    t: (key: string) => string,
): React.ReactNode => {
    // Check if this is a dialog with dialog lines
    if (audio.isDialog && audio.dialogLines && audio.dialogLines.length > 0) {
        const voiceItems = new Map<string, VoiceLookupItem>();

        audio.dialogLines.forEach((line) => {
            if (line.voiceAIId) {
                const voice = voiceLookupById.get(line.voiceAIId);
                if (voice) voiceItems.set(voice.id, voice);
            }
            const rawLine = line as any;
            if (rawLine.voiceCloneId) {
                const voice = voiceLookupByCloneId.get(rawLine.voiceCloneId);
                if (voice) voiceItems.set(voice.id, voice);
            } else if (rawLine.voiceClone?.voiceCloneDbId) {
                const voice = voiceLookupByCloneId.get(rawLine.voiceClone.voiceCloneDbId);
                if (voice) voiceItems.set(voice.id, voice);
            }
            if (rawLine.voicePromptId) {
                const voice = voiceLookupByPromptVoiceId.get(rawLine.voicePromptId);
                if (voice) voiceItems.set(voice.id, voice);
            } else if (rawLine.voicePrompt?.promptVoiceDbId) {
                const voice = voiceLookupByPromptVoiceId.get(rawLine.voicePrompt.promptVoiceDbId);
                if (voice) voiceItems.set(voice.id, voice);
            }
        });

        if (voiceItems.size > 0) {
            const voiceNames = Array.from(voiceItems.values()).map((voice) => voice.name);
            if (voiceNames.length > 0) {
                // The language of the content comes first: a text can be translated into Polish and
                // read by a voice declared Portuguese, and it is the text that is Polish. The voice
                // only answers for audios that carry no language of their own.
                const languageCode = audio.language || audio.voiceAI?.language || "";
                const languageLabel = languagesAI?.find((lang) => lang.code === languageCode)?.label || languageCode;
                return (
                    <>
                        {t("recordedBy")} {voiceNames.join(", ")} {t("inLang")} {languageLabel}
                    </>
                );
            }
        }
    }

    const rawAudio = audio as any;
    const voiceItem = findVoiceInLookup(
        audio.voiceAIId,
        audio.voiceCloneId,
        rawAudio.voicePromptId,
        voiceLookupById,
        voiceLookupByCloneId,
        voiceLookupByPromptVoiceId,
    );

    if (voiceItem) {
        const languageCode = audio.language || audio.voiceAI?.language || "";
        const languageLabel = languagesAI?.find((lang) => lang.code === languageCode)?.label || languageCode;
        return (
            <>
                {t("recordedBy")} {voiceItem.name} {t("inLang")} {languageLabel}
            </>
        );
    }

    if (audio.voiceAI) {
        const languageCode = audio.language || audio.voiceAI.language || "";
        const languageLabel = languagesAI?.find((lang) => lang.code === languageCode)?.label || languageCode;
        return (
            <>
                {t("recordedBy")} {audio.voiceAI.name} {t("inLang")} {languageLabel}
            </>
        );
    }

    if (audio.audioUrl) return t("noInformationAvailable");
    return t("noAudioAvailable");
};

const SortableAudioItem: React.FC<
    SortableAudioItemProps & {
        voiceLookupById: Map<string, VoiceLookupItem>;
        voiceLookupByCloneId: Map<string, VoiceLookupItem>;
        voiceLookupByPromptVoiceId: Map<string, VoiceLookupItem>;
        languagesAI: any[];
        isSelected: boolean;
        onToggleSelected: (audioId: string) => void;
        /** Derived sound design state; undefined when there is none either way. */
        compositionState?: CompositionState;
        onOpenAudioEditing: (audioId: string) => void;
        /** Reordering only makes sense on one language: positions are per audio, not per point. */
        disableDrag?: boolean;
    }
> = ({
    audio,
    setSelectedVoice,
    setIsModalOpen,
    t,
    removeAudioAI,
    refreshProject,
    showAudioPlayer,
    projectId,
    selectedVoice,
    closeAudioPlayer,
    voiceLookupById,
    voiceLookupByCloneId,
    voiceLookupByPromptVoiceId,
    languagesAI,
    isSelected,
    onToggleSelected,
    compositionState,
    onOpenAudioEditing,
    disableDrag = false,
}) => {
    const [editingAudioId, setEditingAudioId] = useState<string | null>(null);
    const [editedAudioName, setEditedAudioName] = useState<string>("");
    const navigate = useNavigate();

    const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: audio.id });

    const style: React.CSSProperties = {
        transform: CSS.Transform.toString(transform),
        transition: transition || "transform 250ms ease",
    };

    const handleAudioNameChange = async (audioId: string) => {
        const token = localStorage.getItem("token");
        if (!token) return;
        try {
            await updateAudioAIName({ token, audioAIId: audioId, name: editedAudioName });
            setEditingAudioId(null);
            await refreshProject();
        } catch (error) {
            console.error("Failed to update audio name:", error);
            setEditingAudioId(null);
        }
    };

    const langCode = audio.language || audio.voiceAI?.language || "";

    return (
        <div ref={setNodeRef} style={style} {...attributes}>
            <div className={s.row}>
                <div className={s.rowLeft}>
                    {disableDrag ? (
                        <div className={s.dragHandlePlaceholder} />
                    ) : (
                        <div {...listeners} className={s.dragHandle} title={t("dragToReorder") || ""}>
                            <svg width="18" height="18" viewBox="0 0 15 15" fill="currentColor">
                                <path
                                    d="M5 2.5H10M5 5.5H10M5 8.5H10M5 11.5H10"
                                    stroke="currentColor"
                                    strokeWidth="1.5"
                                />
                            </svg>
                        </div>
                    )}
                    <input
                        type="checkbox"
                        className={s.checkbox}
                        checked={isSelected}
                        onChange={() => onToggleSelected(audio.id)}
                        onClick={(e) => e.stopPropagation()}
                        title={t("select")}
                    />

                    {audio.audioUrl ? (
                        <div
                            className={s.playBtn}
                            onClick={() => {
                                setSelectedVoice(audio);
                                showAudioPlayer();
                            }}
                        >
                            <Icon name="ic-play" className={s.playIcon} />
                        </div>
                    ) : (
                        <div className={`${s.playBtn} ${s.playBtnDisabled}`}>
                            <Icon name="ic-play" className={s.playIcon} />
                        </div>
                    )}

                    <div className={s.info}>
                        <div className={s.nameRow}>
                            {editingAudioId === audio.id ? (
                                <TextInput
                                    placeholder={t("nameOfTheNewProject")}
                                    value={editedAudioName}
                                    size="small"
                                    onChange={(e) => setEditedAudioName(e.target.value)}
                                    onKeyPress={(e) => e.key === "Enter" && handleAudioNameChange(audio.id)}
                                />
                            ) : (
                                <>
                                    <h4
                                        className={s.name}
                                        onClick={() => navigate(`/projects/${projectId}/update-item/${audio.id}`)}
                                    >
                                        {audio.name}
                                    </h4>
                                    <Icon
                                        name="ic-pencil"
                                        className={s.editIcon}
                                        onClick={() => navigate(`/projects/${projectId}/update-item/${audio.id}`)}
                                    />
                                </>
                            )}
                        </div>
                        <p className={s.meta}>
                            {renderVoiceInfo(
                                audio,
                                voiceLookupById,
                                voiceLookupByCloneId,
                                voiceLookupByPromptVoiceId,
                                languagesAI,
                                t,
                            )}
                        </p>
                    </div>
                </div>

                <div className={s.rowRight}>
                    {compositionState === "available" && (
                        <button
                            type="button"
                            className={s.dressBadge}
                            title={t("soundDesignToApplyTooltip") || ""}
                            onClick={() => onOpenAudioEditing(audio.id)}
                        >
                            <Icon name="ic-music" className={s.dressBadgeIcon} />
                            {t("soundDesignToApply")}
                        </button>
                    )}
                    {compositionState === "dressed" && (
                        <span className={s.dressedBadge} title={t("soundDesignAppliedTooltip") || ""}>
                            <Icon name="ic-check" className={s.dressBadgeIcon} />
                            {t("soundDesignApplied")}
                        </span>
                    )}
                    {langCode && <span className={s.lang}>{langCode.toUpperCase()}</span>}
                    {audio.text && (
                        <Icon
                            name="ic-file-text"
                            className={s.iconAction}
                            onClick={() => {
                                if (selectedVoice && selectedVoice.id !== audio.id) closeAudioPlayer();
                                setSelectedVoice(audio);
                                setIsModalOpen(true);
                            }}
                        />
                    )}
                    {audio.audioUrl && (
                        <Icon
                            name="ic-download"
                            className={s.iconAction}
                            onClick={async () => {
                                try {
                                    const response = await fetch(audio.audioUrl, { cache: "reload" });
                                    const blob = await response.blob();
                                    const url = URL.createObjectURL(blob);
                                    const link = document.createElement("a");
                                    link.href = url;
                                    link.setAttribute("download", `${audio.name}.mp3`);
                                    document.body.appendChild(link);
                                    link.click();
                                    document.body.removeChild(link);
                                    URL.revokeObjectURL(url);
                                } catch (error) {
                                    console.error("Error downloading audio:", error);
                                }
                            }}
                        />
                    )}
                    <button type="button" className={s.removeBtn} onClick={async () => await removeAudioAI(audio.id)}>
                        {t("remove")}
                    </button>
                </div>
            </div>
        </div>
    );
};

type ContentFilter = "all" | "text" | "audio";

/**
 * Sentinel of the "All languages" entry of the language selector. `Dropdown` already turns it
 * into the empty value, which is the "no language filter" this screen has always understood.
 */
const ALL_LANGUAGES_OPTION = "__none__";

/** Remembers whether the versions of a point are shown grouped, like the language filter is. */
const GROUP_VERSIONS_STORAGE_KEY = "projectsGroupLanguageVersions";

const Projects: React.FC<RefreshProjectProps> = ({ refreshProjects }) => {
    const { id } = useParams<{ id: string }>();
    const [projectData, setProjectData] = useState<Project | null>(null);
    const [, setLoading] = useState<boolean>(true);
    const [, setError] = useState<string | null>(null);
    const token = localStorage.getItem("token");
    const navigate = useNavigate();
    const [selectedVoice, setSelectedVoice] = useState<AudioAI | null>(null);
    const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
    const { data } = useGlobalState();
    const [selectedContentLanguage, setSelectedContentLanguage] = useState<string | null>(null);
    const { t: rawT } = useTranslation();
    const t = rawT as any;
    const [isEditingTitle, setIsEditingTitle] = useState<boolean>(false);
    const [editedProjectName, setEditedProjectName] = useState<string>("");
    const [showAudioPlayer, setShowAudioPlayer] = useState<boolean>(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [voiceLookupById, setVoiceLookupById] = useState<Map<string, VoiceLookupItem>>(new Map());
    const [voiceLookupByCloneId, setVoiceLookupByCloneId] = useState<Map<string, VoiceLookupItem>>(new Map());
    const [voiceLookupByPromptVoiceId, setVoiceLookupByPromptVoiceId] = useState<Map<string, VoiceLookupItem>>(
        new Map(),
    );
    const [selectedTextIds, setSelectedTextIds] = useState<Set<string>>(new Set());
    const [isBulkDeleteOpen, setIsBulkDeleteOpen] = useState<boolean>(false);
    const [search, setSearch] = useState<string>("");
    const [contentFilter, setContentFilter] = useState<ContentFilter>("all");
    const [sortMode, setSortMode] = useState<SortMode>("position");
    // Read once, so the list never renders flat before the stored choice is applied.
    const [groupVersions, setGroupVersions] = useState<boolean>(
        () => localStorage.getItem(GROUP_VERSIONS_STORAGE_KEY) !== "false",
    );
    const toggleSelected = (audioId: string) => {
        setSelectedTextIds((prev) => {
            const next = new Set(prev);
            if (next.has(audioId)) next.delete(audioId);
            else next.add(audioId);
            return next;
        });
    };

    const handleSendToTextToSpeech = () => {
        if (!projectData) return;
        const orderedSelected = (sortedProjectData || []).filter((a) => selectedTextIds.has(a.id) && a.text);
        if (orderedSelected.length === 0) return;
        const importedCapsules = orderedSelected.map((a) => ({
            name: a.name || "",
            text: a.text || "",
            language: a.language || null,
        }));
        navigate("/create/text-to-speech", {
            state: { importedCapsules, projectName: projectData.name || "" },
        });
    };

    // One text becomes the source of a new remix. The remix queue takes a single text,
    // so the action is offered for one text at a time, like the audio editor.
    const handleSendToTextRemix = () => {
        const selected = (sortedProjectData || []).filter((a) => selectedTextIds.has(a.id) && a.text);
        if (selected.length !== 1) return;
        const [audio] = selected;
        navigate("/create/text-remix", {
            state: {
                remixSource: { name: audio.name || "", text: audio.text || "", language: audio.language || null },
            },
        });
    };

    // Straight to the editor on one audio, from the row badge.
    const openAudioEditing = useCallback(
        (audioAIId: string) => {
            navigate("/create/audio-editing", { state: { audioAIId } });
        },
        [navigate],
    );

    // Open one audio in the editor. It loads with its own sound design, or the one of the
    // content it was translated from, resolved when the editor opens. The editor renders a
    // single mix, so the action is offered for one audio at a time.
    const handleSendToAudioEditing = () => {
        const selected = (sortedProjectData || []).filter((a) => selectedTextIds.has(a.id) && a.audioUrl);
        if (selected.length !== 1) return;
        navigate("/create/audio-editing", { state: { audioAIId: selected[0].id } });
    };

    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
    );

    const fetchProject = async (projectId: string) => {
        if (!projectId) {
            setError("Something is wrong");
            setTimeout(() => setError(""), 3000);
            return;
        }
        setLoading(true);
        try {
            if (!token) return;
            const response = await getProject({ token, projectId });
            setProjectData(response);
        } catch (error) {
            console.error("Error fetching project:", error);
            setError(t("errorFetchingProject"));
        }
        setLoading(false);
    };

    const handleDragEnd = async (event: DragEndEvent) => {
        const { active, over } = event;
        if (!active || !over || active.id === over.id) return;
        if (!canReorder) return;

        const currentList = sortedProjectData || [];
        const oldIndex = currentList.findIndex((item) => item.id === active.id);
        const newIndex = currentList.findIndex((item) => item.id === over.id);
        if (oldIndex === -1 || newIndex === -1) return;

        const newFilteredOrder = arrayMove(currentList, oldIndex, newIndex);

        const newAudioAI = projectData!.audioAI.map((item) => {
            const filteredIndex = newFilteredOrder.findIndex((filteredItem) => filteredItem.id === item.id);
            if (filteredIndex !== -1) return { ...item, position: filteredIndex + 1 };
            return item;
        });

        setProjectData({ ...projectData!, audioAI: newAudioAI });

        try {
            const positionUpdates = newFilteredOrder.map((item, index) => ({ id: item.id, position: index + 1 }));
            if (!token) return;
            await updateAudioAIPositionInProjects({ token, modifiedData: positionUpdates });
        } catch (error) {
            console.error("Failed to update positions:", error);
            fetchProject(id!);
        }
    };

    const removeAudioAI = async (audioAIId: string) => {
        try {
            if (!token || !id) return;
            await deleteItemFromProject({ token, projectId: id, itemId: audioAIId, itemType: "audio" });
            await fetchProject(id);
        } catch (error) {
            console.error("Failed to remove item:", error);
        }
    };

    const handleBulkDeleteConfirm = async () => {
        if (!token || !id || selectedTextIds.size === 0) return;
        const ids = Array.from(selectedTextIds);
        try {
            await Promise.all(
                ids.map((itemId) => deleteItemFromProject({ token, projectId: id, itemId, itemType: "audio" })),
            );
            setSelectedTextIds(new Set());
            setIsBulkDeleteOpen(false);
            await fetchProject(id);
        } catch (error) {
            console.error("Failed to bulk delete items:", error);
            setIsBulkDeleteOpen(false);
        }
    };

    const refreshProject = async () => {
        if (id) await fetchProject(id);
    };

    const deleteProjectAI = async (projectId: string) => {
        try {
            if (!token) return;
            await deleteProject({ token, projectId });
            refreshProjects();
            navigate("/create/text");
        } catch (error) {
            console.error("Failed to delete project:", error);
        }
    };

    const handleDeleteConfirm = () => {
        if (id) deleteProjectAI(id);
    };

    // Apply language filter, then content (text/audio) filter, then text search.
    const searchLc = search.trim().toLowerCase();
    const filteredProjectData = (projectData?.audioAI || [])
        .filter((a) => (selectedContentLanguage ? a.language === selectedContentLanguage : true))
        .filter((a) => (contentFilter === "text" ? !!a.text : contentFilter === "audio" ? !!a.audioUrl : true))
        .filter((a) =>
            searchLc
                ? (a.name || "").toLowerCase().includes(searchLc) ||
                  (a.voiceAI?.name || "").toLowerCase().includes(searchLc)
                : true,
        );

    const sortedProjectData = sortAudios(filteredProjectData, sortMode);

    // No language selected means "All languages", the only mode where grouping the versions of a
    // point changes anything — on a single language there is at most one version per point to show.
    const isAllLanguages = !selectedContentLanguage;
    // Grouped whatever the toggle says: what it holds decides whether the toggle is worth offering,
    // so computing it only when grouping is on would make the toggle disappear once switched off.
    const languageGroups: AudioLanguageGroup<AudioAI>[] = isAllLanguages
        ? groupAudiosByPoint(sortedProjectData, sortMode)
        : [];
    // No point has a second version on screen: there is nothing for the toggle to reshape.
    const hasLinkedVersions = languageGroups.some((group) => group.items.length > 1);
    const showGroups = isAllLanguages && groupVersions && hasLinkedVersions;

    // Dragging writes `1..n` on the rows on screen and on those only, which is the existing meaning
    // of a reorder: the saved order of one language. Offering it on every language at once would add
    // a second, contradictory scope — a global renumbering makes positions unique, and the next
    // per-language reorder would then pack that language at the top of the combined view. It also
    // means nothing against a sort by title or date, nor on a grouped card.
    const canReorder = !isAllLanguages && sortMode === "position";

    // Which rows are dressed and which could be. Derived server-side from the
    // compositions, so it stays right without a status column to maintain.
    const audioIdsForStates = useMemo(() => sortedProjectData.map((audio) => audio.id), [sortedProjectData]);
    const compositionStates = useCompositionStates(audioIdsForStates);

    // All rows are selectable (for bulk delete); TTS only applies to those with text.
    const selectableAudios = sortedProjectData || [];
    const allSelected = selectableAudios.length > 0 && selectableAudios.every((a) => selectedTextIds.has(a.id));
    const selectedTextCount = (sortedProjectData || []).filter((a) => selectedTextIds.has(a.id) && a.text).length;
    // The editor works on a single audio at a time.
    const selectedAudioCount = (sortedProjectData || []).filter((a) => selectedTextIds.has(a.id) && a.audioUrl).length;
    const toggleSelectAll = () => {
        setSelectedTextIds(() => (allSelected ? new Set<string>() : new Set(selectableAudios.map((a) => a.id))));
    };

    // One row, whether it is listed on its own or under the point it is a version of.
    const renderAudioRow = (audio: AudioAI) => (
        <SortableAudioItem
            key={audio.id}
            audio={audio}
            setSelectedVoice={setSelectedVoice}
            setIsModalOpen={setIsModalOpen}
            t={t}
            removeAudioAI={removeAudioAI}
            refreshProject={refreshProject}
            showAudioPlayer={() => setShowAudioPlayer(true)}
            projectId={id!}
            selectedVoice={selectedVoice}
            closeAudioPlayer={() => setShowAudioPlayer(false)}
            voiceLookupById={voiceLookupById}
            voiceLookupByCloneId={voiceLookupByCloneId}
            voiceLookupByPromptVoiceId={voiceLookupByPromptVoiceId}
            languagesAI={data?.languagesAI || []}
            isSelected={selectedTextIds.has(audio.id)}
            onToggleSelected={toggleSelected}
            compositionState={compositionStates[audio.id]}
            onOpenAudioEditing={openAudioEditing}
            disableDrag={!canReorder}
        />
    );

    const handleTitleChange = async () => {
        try {
            if (!token || !id) return;
            await updateProject({ token, projectId: id, name: editedProjectName });
            setProjectData((prev) => (prev ? { ...prev, name: editedProjectName } : prev));
            setIsEditingTitle(false);
            await refreshProject();
            refreshProjects();
        } catch (error) {
            console.error("Failed to update project name:", error);
            setIsEditingTitle(false);
            setEditedProjectName(projectData?.name || "");
        }
    };

    useEffect(() => {
        const fetchVoicesForLookup = async () => {
            if (!token) return;
            try {
                const voices = await getVoices({ token, language: "EN" });
                const lookupById = new Map<string, VoiceLookupItem>();
                const lookupByCloneId = new Map<string, VoiceLookupItem>();
                const lookupByPromptVoiceId = new Map<string, VoiceLookupItem>();

                voices.forEach((voice) => {
                    const voiceItem: VoiceLookupItem = { id: voice.id, name: voice.name };
                    lookupById.set(voice.id, voiceItem);
                    if (voice.voiceCloneDbId) lookupByCloneId.set(voice.voiceCloneDbId, voiceItem);
                    if (voice.promptVoiceDbId) lookupByPromptVoiceId.set(voice.promptVoiceDbId, voiceItem);
                });

                setVoiceLookupById(lookupById);
                setVoiceLookupByCloneId(lookupByCloneId);
                setVoiceLookupByPromptVoiceId(lookupByPromptVoiceId);
            } catch (error) {
                console.error("Error fetching voices for lookup:", error);
            }
        };
        fetchVoicesForLookup();
    }, [token]);

    useEffect(() => {
        if (id) fetchProject(id);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const handleLanguageChange = (language: string | null) => {
        setSelectedContentLanguage(language);
        localStorage.setItem("selectedLanguageFilter", language || "");
    };

    const handleGroupVersionsChange = (grouped: boolean) => {
        setGroupVersions(grouped);
        localStorage.setItem(GROUP_VERSIONS_STORAGE_KEY, String(grouped));
    };

    useEffect(() => {
        const storedLanguage = localStorage.getItem("selectedLanguageFilter");
        if (storedLanguage) setSelectedContentLanguage(storedLanguage);
    }, []);

    return (
        <div className={s.page}>
            <div className={s.headerTop}>
                <h1 className={s.title}>{t("myProjects")}</h1>
                <p className={s.subtitle}>{t("listenAndDownloadYourAudioCreation")}</p>
            </div>

            <div className={s.projectBar}>
                <div className={s.projectBarLeft}>
                    {isEditingTitle ? (
                        <TextInput
                            placeholder={t("nameOfTheNewProject")}
                            value={editedProjectName}
                            size="small"
                            onChange={(e) => setEditedProjectName(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === "Enter") handleTitleChange();
                                if (e.key === "Escape") {
                                    setEditedProjectName(projectData?.name || "");
                                    setIsEditingTitle(false);
                                }
                            }}
                            onBlur={() => handleTitleChange()}
                            autoFocus
                        />
                    ) : (
                        <>
                            <h2 className={s.projectName}>
                                {projectData?.name} ({projectData?.audioAI?.length || 0})
                            </h2>
                            <Icon
                                name="ic-pencil"
                                className={s.editIcon}
                                onClick={() => {
                                    setEditedProjectName(projectData?.name || "");
                                    setIsEditingTitle(true);
                                }}
                            />
                            <button
                                type="button"
                                className={s.addLink}
                                onClick={() => navigate(`/projects/${id}/add-item`)}
                            >
                                {t("add")}
                            </button>
                        </>
                    )}
                </div>
                <button type="button" className={s.dangerBtn} onClick={() => setIsDeleteModalOpen(true)}>
                    {t("removeProject")}
                </button>
            </div>

            <div className={`${s.toolbar} ${s.toolbarWithSort}`}>
                <div className={s.search}>
                    <Icon name="ic-search" className={s.searchIcon} />
                    <input
                        className={s.searchInput}
                        type="text"
                        placeholder={t("searchByTitleVoiceLanguage")}
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                </div>
                <div style={{ minWidth: 180 }}>
                    <Dropdown
                        label=""
                        value={selectedContentLanguage || ""}
                        options={[
                            { value: ALL_LANGUAGES_OPTION, label: t("allLanguages") },
                            ...(data?.languagesAI?.map((l: { code: string; label: string }) => ({
                                value: l.code,
                                label: l.label,
                            })) || []),
                        ]}
                        onChange={handleLanguageChange}
                        placeholder={t("selectALanguage")}
                        loading={false}
                        error={false}
                    />
                </div>
                <div style={{ minWidth: 180 }}>
                    <Dropdown
                        label=""
                        value={sortMode}
                        options={[
                            { value: "position", label: t("sortByManualOrder") },
                            { value: "title", label: t("sortByTitle") },
                            { value: "createdAt", label: t("sortByCreationDate") },
                        ]}
                        onChange={(value: string) => setSortMode((value as SortMode) || "position")}
                        placeholder={t("sortBy")}
                        loading={false}
                        error={false}
                    />
                </div>
                <div className={s.pills}>
                    {(["all", "text", "audio"] as ContentFilter[]).map((f) => (
                        <button
                            key={f}
                            type="button"
                            className={`${s.pill} ${contentFilter === f ? s.pillActive : ""}`}
                            onClick={() => setContentFilter(f)}
                        >
                            {f === "all" ? t("filterAll") : f === "text" ? t("filterWithText") : t("filterWithAudio")}
                        </button>
                    ))}
                </div>
            </div>

            {hasLinkedVersions && (
                <div className={s.groupToggleRow}>
                    <ToggleSwitch checked={groupVersions} onChange={handleGroupVersionsChange} />
                    <span className={s.groupToggleLabel}>{t("groupLanguageVersions")}</span>
                </div>
            )}

            {selectableAudios.length > 0 && (
                <div className={s.selectAllRow}>
                    <label className={s.selectAllLabel}>
                        <input
                            type="checkbox"
                            className={s.checkbox}
                            checked={allSelected}
                            onChange={toggleSelectAll}
                        />
                        {t("selectAll")}
                    </label>
                    {!canReorder && selectedTextIds.size === 0 && (
                        <span className={s.toolbarHint}>
                            {isAllLanguages ? t("dragNeedsOneLanguage") : t("dragNeedsManualOrder")}
                        </span>
                    )}
                    {selectedTextIds.size > 0 && (
                        <div className={s.selectActions}>
                            {selectedTextCount > 0 && (
                                <button
                                    type="button"
                                    className={s.ttsBtn}
                                    title={t("sendSelectedToTextToSpeechTooltip") || ""}
                                    onClick={handleSendToTextToSpeech}
                                >
                                    {t("textToSpeech")} ({selectedTextCount})
                                </button>
                            )}
                            {selectedTextCount > 0 && (
                                <button
                                    type="button"
                                    className={s.editBtn}
                                    title={
                                        selectedTextCount > 1
                                            ? t("textRemixSelectOnlyOne") || ""
                                            : t("sendSelectedToTextRemixTooltip") || ""
                                    }
                                    onClick={handleSendToTextRemix}
                                    disabled={selectedTextCount > 1}
                                >
                                    <Icon name="ic-shuffle" className={s.editBtnIcon} />
                                    {t("textRemix")} ({selectedTextCount})
                                </button>
                            )}
                            {selectedAudioCount > 0 && (
                                <button
                                    type="button"
                                    className={s.editBtn}
                                    title={
                                        selectedAudioCount > 1
                                            ? t("audioEditingSelectOnlyOne") || ""
                                            : t("sendSelectedToAudioEditingTooltip") || ""
                                    }
                                    onClick={handleSendToAudioEditing}
                                    disabled={selectedAudioCount > 1}
                                >
                                    <Icon name="ic-scissors" className={s.editBtnIcon} />
                                    {t("audioEditing")} ({selectedAudioCount})
                                </button>
                            )}
                            <button type="button" className={s.bulkDeleteBtn} onClick={() => setIsBulkDeleteOpen(true)}>
                                {t("deleteSelected")} ({selectedTextIds.size})
                            </button>
                        </div>
                    )}
                </div>
            )}

            <div className={`${selectedVoice?.audioUrl && showAudioPlayer ? "with-player-margin" : ""}`}>
                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                    <SortableContext items={sortedProjectData?.map((a) => a.id) || []}>
                        {showGroups ? (
                            <div className={s.list}>
                                {/* Every point is a section, single version included, so the list keeps
                                    one rhythm and "1 version" says plainly that nothing is attached
                                    to that row, rather than leaving it in a bucket of leftovers. */}
                                {languageGroups.map((group) => (
                                    <section key={group.key} className={s.versionGroup}>
                                        <h3
                                            className={s.versionGroupTitle}
                                            // Only a point that really holds several versions has
                                            // something to explain about how they were recognised.
                                            title={
                                                group.items.length > 1
                                                    ? t("versionsGroupedByTranslation") || ""
                                                    : undefined
                                            }
                                        >
                                            <span className={s.versionGroupName}>{group.title}</span>
                                            <span className={s.versionGroupCount}>
                                                {t("languageVersionsCount", { count: group.items.length })}
                                            </span>
                                        </h3>
                                        {group.items.map((audio) => renderAudioRow(audio))}
                                    </section>
                                ))}
                            </div>
                        ) : (
                            <div className={s.list}>{sortedProjectData?.map((audio) => renderAudioRow(audio))}</div>
                        )}
                    </SortableContext>
                </DndContext>
            </div>

            {selectedVoice?.audioUrl && showAudioPlayer && (
                <div className={audioFooter.section}>
                    <AudioPlayerRewind
                        title={selectedVoice.name}
                        src={selectedVoice.audioUrl}
                        isAutoPlay={true}
                        onClickAddToProject={() => {}}
                        showAddtoProject={false}
                    />
                </div>
            )}

            {isModalOpen && selectedVoice && (
                <TextModal
                    isOpen={isModalOpen}
                    onClose={() => setIsModalOpen(false)}
                    title={selectedVoice.name}
                    content={selectedVoice.text || ""}
                />
            )}

            <DeleteModal
                isOpen={isDeleteModalOpen}
                onClose={() => setIsDeleteModalOpen(false)}
                onConfirm={handleDeleteConfirm}
                message={t("deleteProjectConfirmation")}
                title={t("deleteProject")}
            />

            <DeleteModal
                isOpen={isBulkDeleteOpen}
                onClose={() => setIsBulkDeleteOpen(false)}
                onConfirm={handleBulkDeleteConfirm}
                message={t("deleteSelectedConfirmation", { count: selectedTextIds.size })}
                title={t("deleteSelected")}
            />
        </div>
    );
};

export default Projects;
